// frontend/src/components/thumbnails/TaleThumbnailsTab.tsx

import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFinishedThumbnails, type FinishedThumbnail } from '@/hooks/useFinishedThumbnails';
import ThumbnailCard from './ThumbnailCard';

type Props = { taleId: string };

// Cultural context options for AI image generation
const CULTURAL_CONTEXTS = [
  { value: 'universal', label: '🌍 Universal', description: 'Diverse representation, no specific cultural markers' },
  { value: 'west-african', label: '🌍 West African', description: 'Traditional African setting with rich earth tones, baobab trees, traditional textiles' },
  { value: 'middle-eastern', label: '🏜️ Middle Eastern', description: 'Biblical desert setting with ancient architecture, olive trees, traditional robes' },
  { value: 'asian', label: '🏯 Asian', description: 'Traditional Asian elements with pagodas, cherry blossoms, harmonious natural settings' },
  { value: 'european', label: '🏰 European', description: 'Medieval/Renaissance architecture, stone churches, rolling green hills' },
];

export default function TaleThumbnailsTab({ taleId }: Props) {
  const { items, loading, error, refresh, deleteFinishedThumbnail } = useFinishedThumbnails(taleId);
  const navigate = useNavigate();
  const [culturalContext, setCulturalContext] = useState('universal');
  const [generating, setGenerating] = useState(false);
  const [showBgSection, setShowBgSection] = useState(true);
  
  // Background images state with real-time updates
  type BgImage = {
    id: string;
    status: 'queued' | 'generating' | 'uploading' | 'generated' | 'error';
    prompt?: string;
    r2Key?: string | null;
    imageType?: string;
    progress?: number;
    lastError?: string;
  };
  
  const [backgroundImages, setBackgroundImages] = useState<BgImage[]>([]);
  const [bgLoading, setBgLoading] = useState(false);

  // Fetch backgrounds (fetch all, filter by having a usable file)
  const fetchBackgrounds = async () => {
    setBgLoading(true);
    try {
      const res = await fetch(`/api/images/${taleId}?type=all`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data && data.items) {
        setBackgroundImages(data.items);
      }
    } catch (err) {
      console.error('Failed to fetch backgrounds:', err);
    } finally {
      setBgLoading(false);
    }
  };

  // Real-time updates via SSE
  useEffect(() => {
    let closed = false;
    let sse: EventSource | null = null;
    let pollTimer: number | null = null;

    const startSSE = () => {
      try {
        sse = new EventSource(`/api/images/stream?taleId=${taleId}&type=all`);
        
        sse.addEventListener('snapshot', (e: MessageEvent) => {
          if (closed) return;
          try {
            const data = JSON.parse(e.data);
            setBackgroundImages(data);
          } catch (err) {
            console.error('SSE snapshot error:', err);
          }
        });

        sse.addEventListener('update', (e: MessageEvent) => {
          if (closed) return;
          try {
            const update = JSON.parse(e.data);
            const imageId = update.imageId || update.id;
            if (!imageId) return;
            
            setBackgroundImages(prev => {
              const existing = prev.find(img => img.id === imageId);
              if (existing) {
                return prev.map(img => 
                  img.id === imageId ? { ...img, ...update } : img
                );
              } else {
                return [...prev, { id: imageId, ...update } as BgImage];
              }
            });
          } catch (err) {
            console.error('SSE update error:', err);
          }
        });

        sse.onerror = () => {
          sse?.close();
          if (!closed) startPolling();
        };
      } catch (err) {
        console.error('SSE connection error:', err);
        if (!closed) startPolling();
      }
    };

    // Fallback polling
    let since = Date.now();
    const startPolling = () => {
      if (closed || pollTimer) return;
      
      const poll = async () => {
        try {
          const res = await fetch(`/api/images/${taleId}/updates?since=${since}&type=all`, {
            credentials: 'include'
          });
          const data = await res.json();
          
          if (data.updates && data.updates.length > 0) {
            data.updates.forEach((update: any) => {
              const imageId = update.imageId || update.id;
              if (!imageId) return;
              
              setBackgroundImages(prev => {
                const existing = prev.find(img => img.id === imageId);
                if (existing) {
                  return prev.map(img => 
                    img.id === imageId ? { ...img, ...update } : img
                  );
                } else {
                  return [...prev, { id: imageId, ...update } as BgImage];
                }
              });
            });
          }
          
          since = data.now || Date.now();
        } catch (err) {
          console.error('Polling error:', err);
        }
      };
      
      poll();
      pollTimer = window.setInterval(poll, 3000);
    };

    // Initial fetch
    fetchBackgrounds();
    
    // Start SSE
    startSSE();

    return () => {
      closed = true;
      sse?.close();
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [taleId]);

  const onDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this finished thumbnail?')) {
      try {
        await deleteFinishedThumbnail(id);
      } catch (error) {
        console.error('Failed to delete thumbnail:', error);
        alert('Failed to delete thumbnail');
      }
    }
  };

  const onOpenWorkspace = () => {
    navigate(`/tales/${taleId}/thumbnails/workspace`);
  };

  // Generate new background with cultural context
  const generateBackground = async () => {
    setGenerating(true);
    try {
      const res = await fetch('/api/thumbnails/generate-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          taleId,
          title: 'Background Image',
          topic: 'Bible Story',
          audience: 'Families',
          style: 'storybook',
          culturalContext
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('Background generation queued:', data);
        alert(`✨ New ${CULTURAL_CONTEXTS.find(c => c.value === culturalContext)?.label} background is being generated! Refresh in a moment to see it.`);
        // Refresh after a short delay
        setTimeout(() => {
          fetchBackgrounds();
        }, 2000);
      } else {
        const error = await res.text();
        console.error('Failed to generate background:', error);
        alert('Failed to generate background. Please try again.');
      }
    } catch (err) {
      console.error('Failed to generate background:', err);
      alert('Failed to generate background. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Filter for usable background images (any generated image with a file)
  const sceneImages = useMemo(() =>
    (backgroundImages || []).filter((img: any) =>
      img.status === 'generated' && (img.r2Key || img.id) // has a file to show
    ),
    [backgroundImages]
  );

  // Count statuses
  const bgStats = useMemo(() => {
    const queued = sceneImages.filter(img => img.status === 'queued').length;
    const generating = sceneImages.filter(img => img.status === 'generating' || img.status === 'uploading').length;
    const generated = sceneImages.filter(img => img.status === 'generated').length;
    const error = sceneImages.filter(img => img.status === 'error').length;
    return { queued, generating, generated, error };
  }, [sceneImages]);



  const list = useMemo(() => items ?? [], [items]);

  return (
    <div className="space-y-6">
      {/* Background Images Section */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">AI Background Images</h3>
            <button
              onClick={() => setShowBgSection(!showBgSection)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {showBgSection ? '▼' : '▶'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchBackgrounds}
              className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm"
              disabled={bgLoading}
            >
              Refresh
            </button>
          </div>
        </div>

        {showBgSection && (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Generate AI background images to use in your thumbnails. Choose a cultural context that matches your story.
            </p>

            {/* Cultural Context Selector */}
            <div className="mb-4">
              <label htmlFor="cultural-context-select" className="text-sm font-medium mb-2 block">Cultural Context</label>
              <select
                id="cultural-context-select"
                title="Select cultural context for AI-generated backgrounds"
                className="w-full md:w-auto px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                value={culturalContext}
                onChange={(e) => setCulturalContext(e.target.value)}
              >
                {CULTURAL_CONTEXTS.map(context => (
                  <option key={context.value} value={context.value}>
                    {context.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {CULTURAL_CONTEXTS.find(c => c.value === culturalContext)?.description}
              </p>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateBackground}
              disabled={generating || !culturalContext}
              className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {generating ? '⏳ Generating...' : '✨ Generate Background'}
            </button>

            {/* Background Images Grid */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">
                  Background Images ({sceneImages.length})
                </div>
                {(bgStats.queued > 0 || bgStats.generating > 0 || bgStats.generated > 0) && (
                  <div className="text-xs text-muted-foreground">
                    {bgStats.generated > 0 && `✅ ${bgStats.generated} ready`}
                    {bgStats.generating > 0 && ` • ⏳ ${bgStats.generating} generating`}
                    {bgStats.queued > 0 && ` • ⏱️ ${bgStats.queued} queued`}
                    {bgStats.error > 0 && ` • ❌ ${bgStats.error} error`}
                  </div>
                )}
              </div>
              
              {bgLoading && (
                <div className="text-sm text-muted-foreground py-4">Loading backgrounds...</div>
              )}

              {!bgLoading && sceneImages.length === 0 && (
                <div className="text-sm text-muted-foreground py-8 text-center border-2 border-dashed border-border rounded-lg">
                  <p className="mb-2">No background images yet.</p>
                  <p className="text-xs">Generate some backgrounds to use in your thumbnails.</p>
                </div>
              )}

              {!bgLoading && sceneImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {sceneImages.map((img: any) => (
                    <div 
                      key={img.id} 
                      className={`relative border border-border rounded-xl p-3 transition-all ${
                        img.status === 'generating' ? 'ring-2 ring-blue-500/35 shadow-lg' : ''
                      }`}
                    >
                      {/* Status badge and spinner */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                          img.status === 'generated' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' :
                          img.status === 'uploading' ? 'bg-blue-500/10 text-blue-300 border-blue-500/30' :
                          img.status === 'generating' ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' :
                          img.status === 'error' ? 'bg-rose-500/10 text-rose-300 border-rose-500/30' :
                          'bg-slate-500/10 text-slate-300 border-slate-500/30'
                        }`}>
                          {img.status}
                        </span>
                        {['queued', 'generating', 'uploading'].includes(img.status) && (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>

                      {/* Image preview */}
                      <div className="h-32 rounded overflow-hidden bg-neutral-900 cursor-pointer hover:opacity-90 transition-opacity">
                        {img.r2Key && img.status === 'generated' ? (
                          <img
                            src={`/api/images/file/${img.id}`}
                            alt={img.prompt || 'Background'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400">
                            {img.status === 'generating' ? 'Generating...' : 
                             img.status === 'uploading' ? 'Uploading...' :
                             img.status === 'queued' ? 'Queued' : 
                             img.status === 'error' ? 'Error' : 'Ready'}
                          </div>
                        )}
                      </div>

                      {/* Progress bar */}
                      {img.progress && img.progress > 0 && img.progress < 100 && (
                        <div className="absolute left-0 bottom-0 h-1 bg-current opacity-30" style={{ width: `${img.progress}%` }} />
                      )}

                      {/* Prompt text */}
                      <div className="mt-2 text-[11px] line-clamp-2 opacity-70">
                        {img.prompt?.slice(0, 100) || 'Background image'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Finished Thumbnails Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Finished Thumbnails</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={onOpenWorkspace}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-sm font-medium"
              type="button"
            >
              🎨 Open Workspace
            </button>

            <button
              className="rounded-lg bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
              onClick={refresh}
            >
              Refresh
            </button>
          </div>
        </div>

        {loading && <div className="text-sm text-zinc-400">Loading finished thumbnails…</div>}
        {error && <div className="text-sm text-red-400">Error: {error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((item) => (
          <div key={item.id} className="relative group">
            <div className="aspect-video rounded-lg overflow-hidden bg-zinc-800 border border-zinc-700">
              {item.pngUrl ? (
                <img 
                  src={item.pngUrl} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-400">
                  No preview
                </div>
              )}
            </div>
            
            <div className="mt-2">
              <div className="text-sm font-medium text-zinc-200">{item.title}</div>
              {item.subtitle && (
                <div className="text-xs text-zinc-400">{item.subtitle}</div>
              )}
              <div className="text-xs text-zinc-500 mt-1">Slot {item.slotNumber}</div>
            </div>

            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => onDelete(item.id)}
                className="bg-red-600 hover:bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                title="Delete thumbnail"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {!loading && list.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <div className="text-sm text-zinc-500 mb-4">
            No finished thumbnails yet.
          </div>
          <button
            onClick={onOpenWorkspace}
            className="rounded-lg bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-sm font-medium"
          >
            🎨 Create Your First Thumbnail
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
